import { Alert, Descriptions, Form, Modal, Typography } from 'antd';
import { useEffect, useState } from 'react';
import INTERNAL_LINKS from '../../enums/InternalLinks';
import PdfViewer from '../../components/PdfViewer';
import {
  generalCertificatesContractTable,
  generalCertificatesOrganizations,
  generalCertificatesTable,
  getContractor,
  getGeneral as getGeneralUrl,
  postGeneral as createGeneralCertificate,
} from '../../api/module/ct_certifications';
import { AjaxTable, CustomButton, CustomSelector } from '../../components';
import { catchExceptionSRate } from '../../utils/helpers/helpers';
import BreadcrumbContainer from '../../containers/Breadcrumb';
import { Link } from 'react-router-dom';

const { Title, Text } = Typography;
const breadcrumbItems = [
  {
    text: 'Certificaciones',
    to: INTERNAL_LINKS.CT_CERTIFICATIONS,
  },
  {
    text: 'Certificaciones generales',
  },
];

const AddModal = ({ resume_id, onFinish, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  async function onSelfFinish(data) {
    try {
      setLoading(true);
      await onFinish({ ...data, tercero: resume_id });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  const columns = [
    {
      title: 'No. Contrato',
      dataIndex: 'contrato_num',
      width: '7em',
      align: 'center',
    },
    {
      title: 'Cargo',
      dataIndex: 'rol',
    },
  ];

  if (!resume_id) return null;
  return (
    <Modal
      open={true}
      footer={
        <CustomButton
          onClick={() => form.submit()}
          text="Generar"
          loading={loading}
        />
      }
      styles={{ footer: { display: 'flex', justifyContent: 'center' } }}
      title="Generación de Certificación general"
      onCancel={onCancel}
      width={'80%'}
    >
      <Form
        form={form}
        onFinish={onSelfFinish}
        layout="vertical"
        requiredMark={false}
        preserve={false}
      >
        <Form.Item
          label="Por favor seleccione la entidad contratante de la cual requiere la certificación"
          name="organizacion"
          valuePropName="initialValue"
          rules={[{ required: true, message: 'Requerido' }]}
        >
          <CustomSelector
            endpointFetch={async () =>
              await generalCertificatesOrganizations(resume_id)
            }
            selectorKey={resume_id}
          />
        </Form.Item>
        <Text>
          La certificación generada mostrará los siguientes datos:{' '}
          <b>Estado del contrato, Objeto, Duración, Valor y Obligaciones</b>
        </Text>
        <br />
        <Text>
          Estos son los contratos que se incluirán en la certificación a generar
        </Text>
        <Form.Item noStyle dependencies={['organizacion']}>
          {({ getFieldValue }) => {
            const org = getFieldValue('organizacion');
            if (!org) return null;
            return (
              <AjaxTable
                columns={columns}
                scroll={{ x: false }}
                endpoint={generalCertificatesContractTable}
                query={{
                  tercero: resume_id,
                  organizacion: org,
                }}
              />
            );
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
};

const CTGeneralCertifications = ({ match, createPerm }) => {
  const [resumeData, setResumeData] = useState();
  const [reloadSource, setReloadSource] = useState(false);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [pdfUrl, setPDFUrl] = useState();
  const [formOpen, setFormOpen] = useState();

  useEffect(() => {
    async function fetchData() {
      try {
        const { data: resData } = await getContractor(match.params.tercero_id);
        setResumeData(resData);
      } catch (error) {
        catchExceptionSRate(error);
      }
    }
    fetchData();
  }, []);

  const onOpenForm = () => {
    setFormOpen(resumeData.id);
  };

  const onFinish = async (data) => {
    try {
      await createGeneralCertificate(data);
      setReloadSource({});
      setFormOpen();
    } catch (error) {
      catchExceptionSRate(error);
    }
  };

  async function getPdfURL(id) {
    try {
      setLoadingUrl(true);
      const { data: dataUrl } = await getGeneralUrl(id);
      setPDFUrl(dataUrl.url);
    } catch (error) {
      catchExceptionSRate(error);
    } finally {
      setLoadingUrl(false);
    }
  }

  const columns = [
    {
      title: 'Fecha de generación',
      dataIndex: 'fecha',
    },
    {
      title: 'Entidad contratante',
      dataIndex: 'organizacion_nombre',
    },
    {
      key: 'acciones',
      title: 'Acciones',
      width: '10em',
      render: (_, record) =>
        record.estado_firma === 4 ? (
          <CustomButton
            text="Visualizar"
            type="default"
            htmlType="button"
            block
            onClick={() => getPdfURL(record.id)}
            loading={loadingUrl}
          />
        ) : (
          'Generación en proceso'
        ),
    },
  ];

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        CERTIFICACIONES GENERALES
      </Title>
      <Descriptions
        layout="horizontal"
        column={2}
        bordered
        labelStyle={{ maxWidth: '5rem' }}
      >
        <Descriptions.Item label="Nombre completo / Razón social" span={2}>
          {resumeData?.contratista_nombre}
        </Descriptions.Item>
        <Descriptions.Item label="Tipo de documento">
          {resumeData?.tipo_identificacion_nombre}
        </Descriptions.Item>
        <Descriptions.Item label="Número de documento">
          {resumeData?.identificacion}
        </Descriptions.Item>
      </Descriptions>
      <br />

      {createPerm && (
        <Alert
          type="info"
          showIcon
          message="Información importante"
          description={
            <>
              <Text>
                A continuación se muestra el listado de certificaciones
                generales disponibles para su visualización. En caso de requerir
                una nueva certificación por favor de clic en "Generar nuevo
                certificado"
              </Text>
              <br />
            </>
          }
          action={
            <CustomButton
              htmlType="button"
              text="Generar nuevo certificado"
              onClick={onOpenForm}
              disabled={!resumeData}
            />
          }
        />
      )}
      {resumeData && (
        <AjaxTable
          tableTitle="Listado"
          columns={columns}
          query={`filter[contratos__tercero]=${resumeData.id}`}
          endpoint={generalCertificatesTable}
          reloadSource={reloadSource}
          searchInput
          exportCSV
          reloadButton
        />
      )}
      <AddModal
        resume_id={formOpen}
        onCancel={() => setFormOpen()}
        onFinish={onFinish}
      />

      <PdfViewer
        pdf={pdfUrl}
        onCancel={() => setPDFUrl(undefined)}
        open={pdfUrl}
      />
      <Link to={`${INTERNAL_LINKS.CT_CERTIFICATIONS}`}>
        <CustomButton
          type={'default'}
          className={'back-button'}
          htmlType="button"
          text={'Volver'}
        />
      </Link>
    </>
  );
};

export default CTGeneralCertifications;
